import { format, isToday, isYesterday } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HyperLiquidClient from '../../clients/HyperLiquidClient'
import { ActivityItem } from '../../interfaces/hyperliquid'
import { logger } from '../../logger'
import { groupBy } from '../../utils/miscHelper'
import useConfig from '../useConfig'
import useWallet from '../useWallet'

export interface PerpActivity {
  id: number
  timestamp: Date
  price: number
  size: number
  perpTokenId?: number
  direction: string
  fee: number
  pnl: number
  liquidated: boolean
}

const usePerpActivity = () => {
  const { perpsTokens } = useConfig()
  const [activities, setActivities] = useState<PerpActivity[]>([])
  const { wallet } = useWallet()
  const { t } = useTranslation()

  const mapActivity = (activity: ActivityItem): PerpActivity => {
    const { tid, coin, px, sz, time, dir, fee, builderFee, closedPnl } = activity
    const token = perpsTokens.find((t) => t.name === coin)

    return {
      id: tid,
      perpTokenId: token?.id,
      size: parseFloat(sz),
      price: parseFloat(px),
      timestamp: new Date(time),
      direction: dir,
      fee: parseFloat(fee) + parseFloat(builderFee || '0'),
      pnl: parseFloat(closedPnl),
      liquidated: !!activity.liquidation,
    }
  }

  useEffect(() => {
    if (!wallet.ethAddress) return

    const client = new HyperLiquidClient()
    client
      .getActivity(wallet.ethAddress)
      .then((activities) => setActivities(activities.map(mapActivity)))
      .catch(logger.error)
  }, [wallet])

  const groupActivityByDate = useCallback(() => {
    if (!activities) return []

    return Object.entries(
      groupBy(activities, (activity) => {
        if (isToday(activity.timestamp)) {
          return t('common.today')
        } else if (isYesterday(activity.timestamp)) {
          return t('common.yesterday')
        } else {
          return format(activity.timestamp, 'dd MMMM yyyy')
        }
      })
    )
  }, [activities])

  return { activities, groupActivityByDate }
}

export default usePerpActivity
